<template>
    <BaseSection
        v-bind="$attrs"
    >
        <template
            v-if="!!$slots.title"
            #title
        >
            <slot name="title" />
        </template>

        <template
            v-if="!!$slots.description"
            #intro
        >
            <slot name="description" />
        </template>

        <template
            v-if="showReviewSummary"
            #default
        >
            <ClientOnly>
                <BaseReviewWidget />
            </ClientOnly>
        </template>
    </BaseSection>

    <DelayHydration>
        <div
            v-if="images.length"
            ref="content"
            class="apartment-list__content"
            :style="{ '--background-color': backgroundColor }"
        >
            <slot name="images">
                <BaseImage
                    v-for="(image, index) in images"
                    ref="imageElements"
                    :key="index"
                    class="apartment-list__image"
                    :image="image"
                    sizes="(min-width: 1440px) 836px, (min-width: 768px) 418px, 223px"
                />
            </slot>
            <div
                class="mask"
                :style="{ '--background-color': backgroundColor }"
            />
        </div>
    </DelayHydration>

    <ApartmentListItems
        :apartments="apartments"
        :pending="pending"
        :background-color="props.backgroundColor"
    />
</template>

<script setup lang="ts">
import type { ColorPalette } from '~/composables/useColorPalette';
import { useColorPalette } from '~/composables/useColorPalette';
import { useScrollAnimations } from '~/composables/useScrollAnimations';
import apartmentsQuery from '~/graphql/queries/apartments.graphql';

interface Props {
    propertyId?: string;
    images?: any[];
    backgroundColor?: keyof ColorPalette;
    showReviewSummary?: boolean;
}

const props = withDefaults(defineProps<Props>(),
    {
        propertyId: undefined,
        images: () => [],
        backgroundColor: () => 'lime',
        showReviewSummary: false
    }
);

const propertyId = computed(() => props.propertyId ? Number(props.propertyId) : null);
const { variables } = useCraftGraphql();
delete variables.uri;
variables.id = propertyId;

const response = ref();

if (propertyId.value) {
    response.value = await useLazyAsyncQuery({
        query: apartmentsQuery,
        variables
    });
}

const pending = computed(() => {
    return toValue(response)?.pending;
});

const apartments = computed(() => {
    if (props.propertyId) {
        return toValue(toValue(response)?.data)?.entries;
    } else {
        return null;
    }
});

const { resolveColor } = useColorPalette();
const backgroundColor = computed(() => resolveColor(props.backgroundColor));

const imageElements = ref([]);
const content = ref();

onMounted(() => {
    const { parallax } = useScrollAnimations();

    parallax(imageElements.value?.[0], { trigger: content.value, offset: 1.2 });
    parallax(imageElements.value?.[1], { trigger: content.value, offset: 1.2, direction: 'down' });
    parallax(imageElements.value?.[2], { trigger: content.value, offset: 1.5 });
});

</script>

<style lang="less">
.apartment-list__content {
    .base-grid(var(--grid-maxWidth-page), false);
    grid-template-rows: .5fr 1fr;
    height: auto;

    @media @q-md-min {
        margin-top: -7rem;
    }

    &:after {
        content: '';
        background-color: var(--background-color);
        z-index: -1;
        grid-row: 2 / -1;
        grid-column: 1 / -1;

        @media ( min-width: 1440px ) {
            width: calc(100vw - var(--scrollbar-width));
            margin-left: calc((0px - (100vw - var(--scrollbar-width) - var(--grid-maxWidth-page)) / 2));
        }
    }
}

.apartment-list__caption {
    .typography-small();
    .base-grid-padding(top);
    .base-grid-padding(bottom);
    .base-grid-padding(right);

    grid-row: 1;
    grid-column: third/-1;

    @media @q-sm-max {
        display: none;
    }
}

.apartment-list__image {
    grid-row: 1;

    &:nth-child(1) {
        grid-column: left-side-gutter/left-gutter;
        grid-row: 2/3;
        margin-top: -3rem;

        @media @q-md-min {
            margin-top: -5rem;
        }
    }

    &:nth-child(2) {
        grid-column: second;
        grid-row: 1/3;
        z-index: 3;
    }

    &:nth-child(3) {
        grid-column: third/-1;
        grid-row: 2;
    }

    .base-image__image {
        object-fit: contain;
        object-position: top;
    }
}

.mask {
    background-color: var(--color-cream);
    z-index: 2;
    grid-row: 1 / 1;
    grid-column: 1 / -1;

    @media ( min-width: 1440px ) {
        width: calc(100vw - var(--scrollbar-width));
        margin-left: calc((0px - (100vw - var(--scrollbar-width) - var(--grid-maxWidth-page)) / 2));
    }
}

.apartment-list__header {
    padding: var(--grid-gutter-width) var(--grid-gutter-margin) var(--spacing-xl);
    text-align: center;
}

// Bring the widget to the front to compansate negative z-index of the stars
.base-review-widget {
    position: relative;
    z-index: 1;
}
</style>
